import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { contactApi } from '../services';
import enrichmentApi from '../services/enrichmentApi';
import useAutocomplete from '../hooks/useAutocomplete';
import AutocompleteInput from './AutocompleteInput';
import Icon from './Icon';
import { SourceLink } from './FindingCard';
import { OUTREACH_STAGE_LABELS, industryLabel, peopleLabel, targetAccountLabel } from './utils';
import './RelationshipManagers.css';
import './CompanyDetailPanel.css';
import './ConferencePrepPanel.css';

// "A", "A and B", "A, B and C"
const listNames = (names) => (
  names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
);

const roleLabel = (person) => (person.role === 'to' ? 'Main contact' : 'CC');

// Known-by people first: the first name read is the one someone on the team can introduce
const warmFirst = (people) => (
  [...people].sort((a, b) => Number(b.known_by.length > 0) - Number(a.known_by.length > 0))
);

const normal = (text) => (text || '').trim().toLowerCase();

// A research finding is a talking point while its value is still on file (otherwise its quote is out of
// date), and only when the quote says more than the value: a copied sentence would just repeat About
const isTalkingPoint = (finding, analytics) => (
  normal(finding.value) !== '' && normal(finding.value) === normal(analytics[finding.field])
  && !normal(finding.value).includes(normal(finding.evidence))
);

const PersonCard = ({ person }) => (
  <li className="cp-person">
    <div className="cp-person__head">
      <span className="cp-person__name">{person.name}</span>
      {person.title && <span className="cp-person__title">{person.title}</span>}
      <span className="cp-person__role">{roleLabel(person)}</span>
    </div>
    <p className={`cp-person__known-by${person.known_by.length > 0 ? '' : ' cp-person__known-by--cold'}`}>
      {person.known_by.length > 0
        ? <>Known by <strong>{listNames(person.known_by)}</strong></>
        : 'Nobody on the team knows them yet'}
    </p>
    {person.now_at && (
      <p className="cp-person__moved">
        Now at <strong>{person.now_at}</strong>{person.now_as && ` as ${person.now_as}`}
      </p>
    )}
    <p className="cp-person__reach">
      {person.email || 'No email on file'}
      {person.do_not_call && <span className="cp-person__dnc"><Icon name="alert-triangle" /> Do not call</span>}
    </p>
  </li>
);

/**
 * Read one company in the minute before meeting someone there at a conference: its stage and
 * owner, who on the team already knows people there (the way in), who to reach, and the facts to
 * open with. Read-only.
 */
const ConferencePrepPanel = ({ isOpen, onClose }) => {
  const [company, setCompany] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [researchFindings, setResearchFindings] = useState([]);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);
  // Bumped on each pick and each opening, so a slower, earlier lookup can't overwrite the current one
  const lookupRef = useRef(0);

  const autocomplete = useAutocomplete('company', {
    onSelect: async (picked) => {
      const lookup = ++lookupRef.current;
      setCompany(picked);
      setAnalytics(null);
      setResearchFindings([]);
      setError(null);
      // Research notes are extra: if they fail to load, the rest of the panel still works
      enrichmentApi.subject('company', picked.id)
        .then(state => {
          if (lookup !== lookupRef.current) return;
          setResearchFindings(state.findings.filter(finding => ['accepted', 'applied'].includes(finding.status)));
        })
        .catch(() => {
          // Non-critical, as in CompanyDetailPanel
        });
      try {
        const data = await contactApi.getCompanyAnalytics(picked.id);
        if (lookup === lookupRef.current) setAnalytics(data);
      } catch (err) {
        if (lookup === lookupRef.current) setError(err.message);
      }
    }
  });
  const { clear } = autocomplete;

  // Each opening is a fresh lookup, as in RelationshipManagersPanel
  useEffect(() => {
    if (!isOpen) return;
    lookupRef.current += 1;
    setCompany(null);
    setAnalytics(null);
    setResearchFindings([]);
    setError(null);
    clear();
    dialogRef.current?.focus();
  }, [isOpen, clear]);

  // Escape closes the dialog, but while suggestions are open it only closes them (the search box does that)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || dialogRef.current?.querySelector('.autocomplete-suggestion')) return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const accounts = analytics?.target_accounts || [];
  const managers = analytics?.relationship_managers || [];
  const advisors = analytics?.advisors || [];
  const people = analytics?.target_people || [];
  const atCompany = warmFirst(people.filter(person => !person.now_at));
  const moved = people.filter(person => person.now_at);
  const warmPerson = atCompany.find(person => person.known_by.length > 0);
  const talkingPoints = analytics ? researchFindings.filter(finding => isTalkingPoint(finding, analytics)) : [];

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog" role="dialog" aria-modal="true" aria-labelledby="conference-prep-title"
           ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <h2 id="conference-prep-title"><Icon name="users" /> Conference prep</h2>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>

        {/* Outside the scrolling body, so the suggestions dropdown isn't clipped */}
        <div className="rm-dialog__toolbar">
          <AutocompleteInput
            value={autocomplete.query}
            onChange={autocomplete.setQuery}
            onSelect={autocomplete.handleSelect}
            suggestions={autocomplete.suggestions}
            loading={autocomplete.loading}
            placeholder="Search for a company..."
            showLogos
            itemType="company"
          />
        </div>

        <div className="rm-dialog__body">
          {!company && (
            <p className="placeholder-text">Search a company to see who you know there, who to reach, and the way in.</p>
          )}
          {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
          {company && !analytics && !error && (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          )}

          {analytics && (
            <>
              <div className="cp-company">
                <h3 className="cp-company__name">{company.name}</h3>
                <p className="cp-company__meta">
                  {[industryLabel(analytics.industry), analytics.hq_location, `${peopleLabel(analytics.people_count)} on file`]
                    .filter(Boolean).join(' · ')}
                  {analytics.domain && (
                    <>
                      {' · '}
                      <a href={`https://${analytics.domain}`} target="_blank" rel="noopener noreferrer">{analytics.domain}</a>
                    </>
                  )}
                </p>
                <div className="cp-chips">
                  {accounts.map(account => (
                    <span className="cp-chip" key={account.id}>
                      {account.icp} · {OUTREACH_STAGE_LABELS[account.outreach_stage] || 'No outreach recorded'}
                    </span>
                  ))}
                  {managers.length > 0 && (
                    <span className="cp-chip">Managed by {listNames(managers.map(manager => manager.name))}</span>
                  )}
                  {accounts.length === 0 && <span className="cp-chip">Not on a target list</span>}
                </div>
                <p className="cp-team">
                  {advisors.length > 0
                    ? `Team connections: ${advisors.map(advisor => `${advisor.name} (${peopleLabel(advisor.people)})`).join(', ')}`
                    : 'No one on the team has connections here yet.'}
                </p>
              </div>

              {warmPerson && (
                <p className="cp-callout">
                  Start with <strong>{warmPerson.name}</strong> ({roleLabel(warmPerson)}), known by {listNames(warmPerson.known_by)}.
                </p>
              )}
              {!warmPerson && atCompany.length > 0 && (
                <p className="cp-callout cp-callout--cold">
                  Nobody on the team knows the people to reach here yet, so the first approach is cold.
                </p>
              )}

              <section className="cp-section">
                <h4>Who to reach <span className="cp-section__count">{atCompany.length}</span></h4>
                {atCompany.length > 0 ? (
                  <ul className="cp-people">
                    {atCompany.map(person => <PersonCard key={person.contact_id} person={person} />)}
                  </ul>
                ) : moved.length > 0 ? (
                  <p className="rm-empty">Everyone on the target list has moved on. See below.</p>
                ) : (
                  <p className="rm-empty">
                    No one on a target list for {company.name} yet. This one needs research before the conference.
                  </p>
                )}
              </section>

              {moved.length > 0 && (
                <section className="cp-section">
                  <h4>Moved on <span className="cp-section__count">{moved.length}</span></h4>
                  <ul className="cp-people">
                    {moved.map(person => <PersonCard key={person.contact_id} person={person} />)}
                  </ul>
                </section>
              )}

              {accounts.length > 0 && (
                <section className="cp-section">
                  <h4>Our plan</h4>
                  {accounts.map(account => (
                    <div className="company-target-account" key={account.id}>
                      <div className="company-target-account__icp">
                        {account.icp_rank ? `${account.icp_rank}. ` : ''}{account.icp}
                      </div>
                      <div className="company-target-account__meta">{targetAccountLabel(account, { withManagers: false })}</div>
                      {account.lead_offer && <div><strong>Lead offer:</strong> {account.lead_offer}</div>}
                      {account.route && <div><strong>Route:</strong> {account.route}</div>}
                      {account.fit_notes && <p className="company-target-account__notes">{account.fit_notes}</p>}
                      {account.status && <p className="company-target-account__notes">{account.status}</p>}
                    </div>
                  ))}
                </section>
              )}

              {talkingPoints.length > 0 && (
                <section className="cp-section">
                  <h4>Talking points</h4>
                  <ul className="cp-findings">
                    {talkingPoints.map(finding => (
                      <li className="cp-finding" key={finding.id}>
                        <div className="cp-finding__label">
                          {finding.label} · {finding.status === 'accepted' ? 'Reviewed' : 'Auto-filled'}
                        </div>
                        <blockquote className="cp-finding__evidence">
                          {finding.evidence} <SourceLink url={finding.source_url} />
                        </blockquote>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(analytics.description || analytics.offerings || analytics.reach_out_roles) && (
                <section className="cp-section">
                  <h4>About</h4>
                  {analytics.description && <p className="company-about"><span>{analytics.description}</span></p>}
                  {analytics.offerings && (
                    <p className="company-about"><Icon name="briefcase" /> <span>{analytics.offerings}</span></p>
                  )}
                  {analytics.reach_out_roles && (
                    <p className="company-about"><Icon name="user-check" /> <span>Roles to reach: {analytics.reach_out_roles}</span></p>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default ConferencePrepPanel;
